import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Paper, Typography, useTheme } from "@mui/material";
import { workflowDiagram } from "../api/workflowDiagram";
import { WorkflowDetail } from "../api/workflows";

interface Props {
  wf: WorkflowDetail;
}

// Mermaid is heavy (~600 KB). We only need it on workflow detail
// pages, so lazy-import it the first time the component mounts;
// future renders reuse the cached module.
let _mermaidModulePromise: Promise<typeof import("mermaid")> | null = null;
function loadMermaid() {
  if (!_mermaidModulePromise) {
    _mermaidModulePromise = import("mermaid");
  }
  return _mermaidModulePromise;
}

// Render the workflow's job graph as a mermaid flowchart. Re-renders
// whenever the workflow data changes (the detail page polls every 5s),
// so colors update as jobs flip succeeded/failed/etc. live.

export function WorkflowDiagram({ wf }: Props) {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === "dark";
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Re-render whenever the diagram text changes (status of any job
  // flipped, new job appeared, etc.). We compute the text in render
  // so the effect's dependency tracks it cheaply.
  const diagramText = workflowDiagram(wf);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadMermaid().then(async (mermaid) => {
      if (cancelled) return;
      // Initialize once per page (idempotent enough — startOnLoad off
      // keeps mermaid from auto-scanning the DOM). Theme picks up the
      // current MUI mode so the diagram doesn't look out of place.
      mermaid.default.initialize({
        startOnLoad: false,
        securityLevel: "strict",
        theme: isDark ? "dark" : "default",
        // ELK renderer respects per-subgraph `direction LR` reliably;
        // mermaid's default dagre layout silently falls back to TB
        // when a subgraph has no internal edges (which we don't —
        // sibling months are independent), making 12-month stages
        // stack vertically when we want them side-by-side.
        flowchart: { htmlLabels: false, defaultRenderer: "elk" },
      });
      try {
        // Unique id per render so mermaid doesn't collide with itself
        // across re-renders. Pulled from a counter-ish source.
        const id = `wf_diag_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
        const { svg } = await mermaid.default.render(id, diagramText);
        if (cancelled) return;
        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
        }
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [diagramText, isDark]);

  if (wf.jobs.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="overline" color="text.secondary">
        Workflow graph
      </Typography>
      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Rendering…
          </Typography>
        </Box>
      )}
      {error && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
          Diagram failed to render: {error}
        </Typography>
      )}
      <Box
        ref={containerRef}
        sx={{
          mt: 1,
          // Mermaid emits its own viewBox-driven sizing; override the
          // wrapper svg to honor our container width and let it scale.
          "& svg": { maxWidth: "100%", height: "auto" },
        }}
      />
    </Paper>
  );
}
